import { useMutation, useQuery } from "@tanstack/react-query";
import { FieldArray, Form, Formik, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { enums } from "../../constants/enum";
import useSupplierService from "../../services/api/supplierService";
import useSupplyService from "../../services/api/supplyService";
import { supplySchema } from "../../schemas/supply";

import {
  Badge,
  Button,
  IconButton,
  Loading,
  PageTitle,
  Section,
} from "../../components";
import {
  DateInput,
  DropArea,
  SelectInput,
  TextInput,
} from "../../components/forms";
import { CloseIcon, MinusIcon, PasteIcon, PlusIcon } from "../../assets/icons";
import "./SuppliesPage.scss";

const FormPersist = () => {
  const { values } = useFormikContext();
  useEffect(() => {
    localStorage.setItem(enums.ADD_SUPPLY_FORM_STORAGE_KEY, JSON.stringify(values));
  }, [values]);
  return null;
};

function AddSupplyPage() {
  const navigate = useNavigate();
  const { getAllSuppliers } = useSupplierService();
  const { createSupplyOrder } = useSupplyService();

  const [initialValues, setInitialValues] = useState({
    supplier: "",
    items: [{ item: "", quantity: "" }],
    payments: [{ amount: "", date: new Date(), method: "", paymentSlip: "", chequeNumber: "" }],
  });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(enums.ADD_SUPPLY_FORM_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure structure validity or merge with defaults if needed
        setInitialValues({
          supplier: parsed.supplier || "",
          items: parsed.items || [{ item: "", quantity: "" }],
          payments: parsed.payments || [{ amount: "", date: new Date(), method: "", paymentSlip: "", chequeNumber: "" }],
        });
      } catch (e) {
        console.error("Failed to parse saved form data", e);
      }
    }
    setIsLoaded(true);
  }, []);

  const { data: supplierData, isLoading: isSuppliersLoading } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => getAllSuppliers("", 1, 100),
  });

  const mutation = useMutation({
    mutationFn: (values) =>
      createSupplyOrder({
        payload: {
          ...values,
          items: values.items.map((i) => ({
            item: i.item,
            quantity: Number(i.quantity),
          })),
          payments: values.payments.map((p) => {
            const { chequeNumber, ...rest } = p;
            return {
              ...rest,
              amount: Number(p.amount),
              ...(p.method === "cheque" && { chequeNumber }),
            };
          }),
        },
      }),
    onSuccess: () => {
      toast.success("Supply record added successfully");
      localStorage.removeItem(enums.ADD_SUPPLY_FORM_STORAGE_KEY);
      navigate("/supplies");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to add supply record",
      );
    },
  });

  if (isSuppliersLoading || !isLoaded) {
    return <Loading variant="fullScreen" />;
  }

  const supplierOptions = [
    { label: "Select Supplier", value: "", disabled: true },
    ...(supplierData?.suppliers || []).map((supplier) => ({
      label: supplier.name,
      value: supplier._id,
    })),
  ];

  const handlePaste = async (setFieldValue, fieldPath) => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setFieldValue(fieldPath, text);
      }
    } catch (err) {
      toast.error("Failed to read clipboard");
    }
  };

  return (
    <div className="page add-supply-page">
      <PageTitle text="Add New Supply Record" prev="/supplies" />
      <Section className="supply-form">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={supplySchema}
          onSubmit={(values) => mutation.mutate(values)}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form>
              <FormPersist />
              <SelectInput
                name="supplier"
                label="Supplier Name"
                values={supplierOptions}
              />

              <FieldArray name="items">
                {({ push, remove }) => (
                  <>
                    {values.items.map((item, index) => (
                      <Section key={index} className="item-input-area">
                        <div className="label-are">
                          <Badge text={`Item ${index + 1}`} />
                          {values.items.length > 1 && (
                            <Badge
                              icon={<CloseIcon className="icon" />}
                              onClick={() => remove(index)}
                              style={{ cursor: "pointer" }}
                            />
                          )}
                        </div>
                        <div className="item-code">
                          <TextInput
                            name={`items[${index}].item`}
                            label="Item Code"
                            placeholder="Type here"
                            className="item-code-input"
                          />
                          <Button
                            text="Paste"
                            type="button"
                            className="paste-btn"
                            icon={<PasteIcon />}
                            onClick={() =>
                              handlePaste(setFieldValue, `items[${index}].item`)
                            }
                          />
                        </div>
                        <div className="item-qty">
                          <IconButton
                            icon={<PlusIcon />}
                            className="action-btn"
                            rounded
                            onClick={() =>
                              setFieldValue(
                                `items[${index}].quantity`,
                                (Number(item.quantity) || 0) + 1,
                              )
                            }
                          />
                          <TextInput
                            type="number"
                            name={`items[${index}].quantity`}
                            className="qty-input"
                            placeholder="00"
                          />
                          <IconButton
                            icon={<MinusIcon />}
                            className="action-btn"
                            rounded
                            onClick={() =>
                              setFieldValue(
                                `items[${index}].quantity`,
                                Math.max(0, (Number(item.quantity) || 0) - 1),
                              )
                            }
                          />
                        </div>
                      </Section>
                    ))}
                    <Button
                      text="Add Item"
                      type="button"
                      className="add-new-item-btn"
                      icon={<PlusIcon />}
                      onClick={() => push({ item: "", quantity: "" })}
                    />
                  </>
                )}
              </FieldArray>

              <FieldArray name="payments">
                {({ push, remove }) => (
                  <>
                    {values.payments.map((payment, index) => (
                      <Section key={index}>
                        <div className="payment-input-area">
                          <div className="label-are">
                            <Badge text={`Payment ${index + 1}`} />
                            <Badge
                              icon={<CloseIcon className="icon" />}
                              onClick={() => remove(index)}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                          <div className="input-group">
                            <div className="amount-area">
                              <span>LKR</span>
                              <TextInput
                                label="Amount"
                                type="currency"
                                name={`payments[${index}].amount`}
                                placeholder="Type here"
                                className="currency-input-field"
                              />
                            </div>
                            <DateInput
                              name={`payments[${index}].date`}
                              label="Date"
                            />
                          </div>
                        </div>
                        <SelectInput
                          name={`payments[${index}].method`}
                          label="Payment Method"
                          className="payment-method"
                          values={[
                            {
                              label: "Select Payment Method",
                              value: "",
                              disabled: true,
                            },
                            ...Object.values(enums.PAYMENT_METHODS).map(
                              (method) => ({
                                label: method,
                                value: method,
                              }),
                            ),
                          ]}
                        />
                        {payment.method === enums.PAYMENT_METHODS.CHEQUE && (
                          <TextInput
                            name={`payments[${index}].chequeNumber`}
                            label="Cheque Number"
                            placeholder="Enter Cheque Number"
                            className="cheque-input"
                          />
                        )}
                        <DropArea
                          name={`payments[${index}].paymentSlip`}
                          label="Payment Slip"
                        />
                      </Section>
                    ))}
                    <Button
                      text="Add Payment"
                      type="button"
                      className="add-new-item-btn"
                      icon={<PlusIcon />}
                      onClick={() =>
                        push({
                          amount: "",
                          date: new Date(),
                          method: "",
                          paymentSlip: "",
                          chequeNumber: "",
                        })
                      }
                    />
                  </>
                )}
              </FieldArray>

              <Button
                text="Add New Supply Record"
                type="submit"
                icon={<PlusIcon />}
                className="submit-button"
              />
            </Form>
          )}
        </Formik>
      </Section>
      <Loading variant="fullScreen" isLoading={mutation.isPending} />
    </div>
  );
}

export default AddSupplyPage;
